import { Box, Typography, Alert, CircularProgress } from '@mui/material'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAppDispatch } from '@store/hooks'
import { loginSuccess, loginFailure } from '@store/slices/authSlice'

// Polyfill URLSearchParams for browser compatibility
declare global {
  interface Window {
    URLSearchParams: typeof URLSearchParams
  }
}

const OAuthCallback: React.FC = () => {
  const [error, setError] = useState('')
  const [isProcessing, setIsProcessing] = useState(true)
  const navigate = useNavigate()
  const dispatch = useAppDispatch()

  useEffect(() => {
    const handleOAuthCallback = () => {
      try {
        // Parse URL parameters. Prefer hash fragment for sensitive OAuth tokens.
        const searchParams = new URLSearchParams(window.location.search)
        const hashParams = window.location.hash.startsWith('#')
          ? new URLSearchParams(window.location.hash.slice(1))
          : new URLSearchParams()
        const params = hashParams.get('accessToken') ? hashParams : searchParams

        const accessToken = params.get('accessToken')
        const userStr = params.get('user')
        const error = searchParams.get('error')
        const message = searchParams.get('message')
        const canceled = searchParams.get('canceled')

        // Handle OAuth cancellation
        if (canceled === 'true') {
          dispatch(loginFailure(''))
          navigate('/auth/login', { 
            state: { 
              info: 'Google sign-in was cancelled. You can try again or use email to sign in.'
            } 
          })
          return
        }

        // Handle OAuth error
        if (error) {
          let friendlyMessage = 'Authentication failed'
          
          switch (error) {
            case 'access_denied':
              friendlyMessage = 'You declined to share your Google account information'
              break
            case 'oauth_not_configured':
              friendlyMessage = 'Google sign-in is not available at the moment'
              break
            case 'auth_failed':
              friendlyMessage = 'Google authentication failed. Please try again'
              break
            default:
              friendlyMessage = message || 'Authentication failed'
          }
          
          dispatch(loginFailure(friendlyMessage))
          navigate('/auth/login', { 
            state: { 
              error: friendlyMessage 
            } 
          })
          return
        }

        if (accessToken && userStr) {
          try {
            const user = JSON.parse(decodeURIComponent(userStr))
            
            // Update Redux state from OAuth callback payload.
            dispatch(loginSuccess({ user, token: accessToken }))
            
            // Redirect to dashboard
            navigate('/app')
          } catch (_err) {
            dispatch(loginFailure('Invalid user data received'))
            navigate('/auth/login', { 
              state: { 
                error: 'Invalid user data received' 
              } 
            })
          }
        } else {
          // Missing required data
          dispatch(loginFailure('Authentication failed - incomplete response'))
          navigate('/auth/login', { 
            state: { 
              error: 'Authentication failed - incomplete response' 
            } 
          })
        }
      } catch (err: any) {
        setError(err?.message || 'Authentication failed')
        setTimeout(() => {
          navigate('/auth/login', { replace: true })
        }, 3000)
      } finally {
        setIsProcessing(false)
      }
    }

    handleOAuthCallback()
  }, [dispatch, navigate])

  if (isProcessing) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center', 
        minHeight: '100vh',
        textAlign: 'center'
      }}>
        <CircularProgress size={60} sx={{ mb: 2 }} />
        <Typography variant="h5" gutterBottom>
          Completing authentication...
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Please wait while we set up your account.
        </Typography>
      </Box>
    )
  }

  if (error) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center', 
        minHeight: '100vh',
        textAlign: 'center',
        px: 2
      }}>
        <Alert severity="error" sx={{ mb: 2, maxWidth: 400 }}>
          {error}
        </Alert>
        <Typography variant="body2" color="text.secondary">
          Redirecting you back to login page...
        </Typography>
      </Box>
    )
  }

  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      alignItems: 'center', 
      justifyContent: 'center', 
      minHeight: '100vh',
      textAlign: 'center'
    }}>
      <Typography variant="h5" gutterBottom color="success.main">
        Authentication Successful!
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Redirecting you to your dashboard...
      </Typography>
    </Box>
  )
}

export default OAuthCallback
